import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, X } from 'lucide-react';

export const BulkUploadDialog = ({
  open, onClose, file, setFile, onUpload, uploading, result
}) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-2xl" data-testid="bulk-upload-dialog">
      <DialogHeader>
        <DialogTitle>Bulk Upload Clients</DialogTitle>
      </DialogHeader>

      {!result ? (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm text-blue-900 mb-2">How It Works</h4>
            <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
              <li>Download the template — it has <strong>Yes/No dropdowns</strong> for Service Tags (purple) and Subtasks (amber)</li>
              <li>Fill client details + select <strong>Yes</strong> for applicable services and subtasks</li>
              <li>Upload — clients are created and auto-assigned to service-linked subtasks + individually marked subtasks</li>
            </ol>
          </div>

          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <div className="space-y-2">
              <Label htmlFor="bulk-file" className="cursor-pointer">
                <div className="text-sm font-medium text-primary hover:underline">Click to select Excel file</div>
                <input id="bulk-file" type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files[0])} className="hidden" data-testid="bulk-file-input" />
              </Label>
              {file && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <span>Selected: {file.name}</span>
                  <Button variant="ghost" size="sm" onClick={() => setFile(null)}><X className="h-3 w-3" /></Button>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={uploading}>Cancel</Button>
            <Button onClick={onUpload} disabled={!file || uploading} data-testid="upload-button">
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-1">Upload Complete</h4>
            <p className="text-sm text-green-800">
              Successfully imported <strong>{result.successful}</strong> out of <strong>{result.successful + result.failed}</strong> clients
            </p>
            {result.total_subtask_assignments > 0 && (
              <p className="text-sm text-green-700 mt-1">Auto-assigned to <strong>{result.total_subtask_assignments}</strong> subtask(s) across boards</p>
            )}
          </div>

          {result.failed > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-semibold text-amber-900 mb-2">Failed Rows ({result.failed})</h4>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {result.failed_clients?.map((fail) => (
                  <div key={`fail-${fail.row}`} className="text-sm text-amber-800 bg-white rounded p-2">
                    <strong>Row {fail.row}:</strong> {fail.error}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.successful > 0 && result.successful_clients?.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 mb-2">Successfully Imported ({result.successful})</h4>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {result.successful_clients.slice(0, 10).map((client) => (
                  <div key={`success-${client.row}`} className="text-xs text-green-800 bg-white rounded px-2 py-1 flex justify-between">
                    <span>Row {client.row}: {client.name} ({client.email})</span>
                    {client.assigned_subtasks > 0 && <span className="text-green-600 font-medium">{client.assigned_subtasks} subtasks linked</span>}
                  </div>
                ))}
                {result.successful_clients.length > 10 && (
                  <p className="text-xs text-green-700 italic pt-1">... and {result.successful_clients.length - 10} more</p>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={onClose} data-testid="close-result-button">Close</Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);
